import React, { useCallback, useEffect, useState } from 'react';
import { ScrollView, StyleSheet, Text, TextInput, TouchableOpacity, View } from 'react-native';
import { useTranslation } from 'react-i18next';
import { useTheme } from '../theme/ThemeContext';
import { useOrg } from '../hooks/useOrg';
import OrgManager from '../services/OrgManager';

const ROLES = ['viewer', 'member', 'admin'];

export default function OrgInviteScreen() {
  const { t } = useTranslation();
  const { theme } = useTheme();
  const { activeOrg, myRole } = useOrg();

  const [email, setEmail] = useState('');
  const [role, setRole] = useState('member');
  const [sending, setSending] = useState(false);
  const [message, setMessage] = useState('');

  const [pending, setPending] = useState([]);
  const [loadingPending, setLoadingPending] = useState(true);

  const canInvite = myRole === 'owner' || myRole === 'admin';

  const loadPending = useCallback(async () => {
    if (!activeOrg || !canInvite) return;
    setLoadingPending(true);
    try {
      const data = await OrgManager.listInvitations(activeOrg.id);
      setPending(data || []);
    } catch (e) {
      console.error('OrgInviteScreen load error:', e);
    } finally {
      setLoadingPending(false);
    }
  }, [activeOrg, canInvite]);

  useEffect(() => { loadPending(); }, [loadPending]);

  const sendInvite = async () => {
    if (!email.trim()) { setMessage(t('app.org.invitePleaseEnterEmail')); return; }
    setSending(true);
    setMessage('');
    try {
      await OrgManager.sendInvitation(activeOrg.id, email.trim(), role);
      setMessage(t('app.org.inviteSentTo', { email: email.trim() }));
      setEmail('');
      loadPending();
    } catch (e) {
      setMessage(`Error: ${e.message}`);
    } finally {
      setSending(false);
    }
  };

  const revoke = async (inviteId) => {
    if (!window.confirm(t('app.org.confirmRevokeInvite'))) return;
    try {
      await OrgManager.revokeInvitation(activeOrg.id, inviteId);
      loadPending();
    } catch (e) {
      alert(`Failed to revoke: ${e.message}`);
    }
  };

  if (!activeOrg) return (
    <View style={styles.center}><Text style={{ color: theme.textSecondary }}>{t('app.org.noActiveOrg')}</Text></View>
  );

  if (!canInvite) return (
    <View style={styles.center}><Text style={{ color: theme.textSecondary }}>{t('app.org.inviteAdminOnly')}</Text></View>
  );

  return (
    <ScrollView style={[styles.container, { backgroundColor: theme.background }]}
                contentContainerStyle={styles.content}>
      <Text style={[styles.title, { color: theme.text }]}>{t('app.org.inviteTitle')}</Text>

      {/* Send form */}
      <View style={[styles.card, { backgroundColor: theme.cardBg, borderColor: theme.border }]}>
        <Text style={[styles.label, { color: theme.textSecondary }]}>{t('app.org.emailAddress')}</Text>
        <TextInput
          style={[styles.input, { backgroundColor: theme.inputBg || theme.cardBg, color: theme.text, borderColor: theme.border }]}
          value={email}
          onChangeText={setEmail}
          placeholder={t('app.org.inviteEmailPlaceholder')}
          placeholderTextColor={theme.textSecondary}
          keyboardType="email-address"
          autoCapitalize="none"
        />

        <Text style={[styles.label, { color: theme.textSecondary, marginTop: 14 }]}>{t('app.org.roleLabel')}</Text>
        <View style={styles.roleRow}>
          {ROLES.map(r => (
            <TouchableOpacity
              key={r}
              onPress={() => setRole(r)}
              style={[
                styles.roleChip,
                { borderColor: theme.border },
                role === r && { backgroundColor: theme.accent, borderColor: theme.accent },
              ]}
            >
              <Text style={{ color: role === r ? '#fff' : theme.textSecondary, fontSize: 13, fontWeight: '500' }}>
                {t(`app.org.role${r.charAt(0).toUpperCase() + r.slice(1)}`)}
              </Text>
            </TouchableOpacity>
          ))}
        </View>

        {message ? (
          <Text style={[styles.message, { color: message.startsWith('Error') ? '#ef4444' : '#22c55e' }]}>
            {message}
          </Text>
        ) : null}

        <TouchableOpacity
          style={[styles.sendBtn, { backgroundColor: theme.accent }]}
          onPress={sendInvite}
          disabled={sending}
        >
          <Text style={styles.sendBtnText}>{sending ? t('app.org.sending') : t('app.org.sendInvitation')}</Text>
        </TouchableOpacity>
      </View>

      {/* Pending invitations */}
      <Text style={[styles.sectionTitle, { color: theme.text }]}>{t('app.org.pendingInvitations')}</Text>
      {loadingPending ? (
        <Text style={{ color: theme.textSecondary }}>{t('app.org.loading')}</Text>
      ) : pending.length === 0 ? (
        <Text style={{ color: theme.textSecondary, fontSize: 13 }}>{t('app.org.noPendingInvitations')}</Text>
      ) : (
        pending.map(inv => (
          <View key={inv.id} style={[styles.inviteRow, { backgroundColor: theme.cardBg, borderColor: theme.border }]}>
            <View style={{ flex: 1 }}>
              <Text style={[styles.inviteEmail, { color: theme.text }]}>{inv.invited_email}</Text>
              <Text style={[styles.inviteMeta, { color: theme.textSecondary }]}>
                {t('app.org.inviteRoleExpires', { role: inv.role, date: new Date(inv.expires_at).toLocaleDateString() })}
              </Text>
            </View>
            <TouchableOpacity onPress={() => revoke(inv.id)} style={styles.revokeBtn}>
              <Text style={{ color: '#ef4444', fontSize: 12 }}>{t('app.org.revoke')}</Text>
            </TouchableOpacity>
          </View>
        ))
      )}
    </ScrollView>
  );
}

const styles = StyleSheet.create({
  container: { flex: 1 },
  content: { padding: 24, maxWidth: 560, alignSelf: 'center', width: '100%' },
  center: { flex: 1, justifyContent: 'center', alignItems: 'center' },
  title: { fontSize: 22, fontWeight: '700', marginBottom: 20 },
  card: { borderWidth: 1, borderRadius: 12, padding: 18, marginBottom: 28 },
  label: { fontSize: 12, fontWeight: '600', marginBottom: 6, textTransform: 'uppercase', letterSpacing: 0.5 },
  input: { borderWidth: 1, borderRadius: 8, paddingHorizontal: 12, paddingVertical: 10, fontSize: 14, height: 42 },
  roleRow: { flexDirection: 'row', gap: 10, marginTop: 4 },
  roleChip: { paddingHorizontal: 16, paddingVertical: 8, borderRadius: 20, borderWidth: 1, cursor: 'pointer' },
  message: { marginTop: 12, fontSize: 13, fontWeight: '500' },
  sendBtn: { marginTop: 16, paddingVertical: 12, borderRadius: 10, alignItems: 'center' },
  sendBtnText: { color: '#fff', fontWeight: '600', fontSize: 14 },
  sectionTitle: { fontSize: 16, fontWeight: '700', marginBottom: 12 },
  inviteRow: {
    flexDirection: 'row', alignItems: 'center', gap: 10,
    borderWidth: 1, borderRadius: 10, padding: 14, marginBottom: 8,
  },
  inviteEmail: { fontSize: 13, fontWeight: '600' },
  inviteMeta: { fontSize: 12, marginTop: 2 },
  revokeBtn: { paddingVertical: 4, paddingHorizontal: 8, cursor: 'pointer' },
});
